// src/roles/store/SiteStockView.jsx
//
// Site-wise Stock Tracking — Phase 1 (read-only).
// Shows site_inventory_summary, a cache derived entirely from
// inventory_transactions.site_id via updateSiteInventorySummary()
// (called only from issueAgainstMR() as of this phase). Legacy issues
// (before this feature shipped) are NOT retroactively attributed to any
// site — this view only reflects transactions made after launch, per
// the detect-and-fallback discipline (CORE_RULES §11) rather than a
// bulk migration.

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { createSiteStockTransfer } from "../../services/inventoryService";

export default function SiteStockView() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [sites,       setSites]       = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [stockRows,   setStockRows]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);

  // ── SITE-TO-SITE TRANSFER (Phase 2) ──
  const [transferRow,   setTransferRow]   = useState(null); // the stockRows item being transferred, or null
  const [transferToSite, setTransferToSite] = useState("");
  const [transferQty,    setTransferQty]    = useState("");
  const [transferRemarks, setTransferRemarks] = useState("");
  const [transferring,   setTransferring]   = useState(false);
  const [transferError,  setTransferError]  = useState("");

  useEffect(() => { fetchSites(); }, []); // eslint-disable-line

  const fetchSites = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(query(
        collection(db, "sites"),
        where("company_id", "==", company_id)
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSites(list);
      if (list.length > 0) {
        setSelectedSite(list[0].id);
        fetchStockForSite(list[0].id);
      }
    } catch (err) {
      console.error("SiteStockView — fetchSites error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockForSite = async (siteId) => {
    if (!siteId) { setStockRows([]); return; }
    try {
      setLoadingStock(true);
      const snap = await getDocs(query(
        collection(db, "site_inventory_summary"),
        where("company_id", "==", company_id),
        where("site_id",    "==", siteId)
      ));
      const rows = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => Number(r.qty || 0) > 0) // hide zero-stock rows — noise
        .sort((a, b) => (a.item_name || "").localeCompare(b.item_name || ""));
      setStockRows(rows);
    } catch (err) {
      console.error("SiteStockView — fetchStockForSite error:", err);
    } finally {
      setLoadingStock(false);
    }
  };

  const handleSiteChange = (siteId) => {
    setSelectedSite(siteId);
    fetchStockForSite(siteId);
  };

  const openTransfer = (row) => {
    setTransferRow(row);
    setTransferToSite("");
    setTransferQty("");
    setTransferRemarks("");
    setTransferError("");
  };

  const closeTransfer = () => {
    setTransferRow(null);
    setTransferError("");
  };

  const submitTransfer = async () => {
    setTransferError("");
    const qty = Number(transferQty || 0);

    if (!transferToSite) { setTransferError("Select a destination site."); return; }
    if (qty <= 0)         { setTransferError("Enter a valid quantity."); return; }
    if (qty > Number(transferRow.qty || 0)) {
      setTransferError(`Cannot transfer more than available (${transferRow.qty} ${transferRow.unit}).`);
      return;
    }

    const toSite = sites.find(s => s.id === transferToSite);
    const user   = JSON.parse(localStorage.getItem("user") || "{}");
    const fromSite = sites.find(s => s.id === selectedSite);

    try {
      setTransferring(true);
      const result = await createSiteStockTransfer({
        company_id,
        from_site_id:   selectedSite,
        from_site_name: fromSite?.site_name || "",
        to_site_id:     transferToSite,
        to_site_name:   toSite?.site_name || "",
        item_id:   transferRow.item_id,
        item_code: transferRow.item_code,
        item_name: transferRow.item_name,
        unit:      transferRow.unit,
        qty,
        requested_by:      user.uid || user.id || "",
        requested_by_name: user.name || "",
        remarks: transferRemarks,
      });
      alert(`Transfer ${result.transfer_no} created — waiting for ${toSite?.site_name || "the destination site"} to accept.`);
      closeTransfer();
      fetchStockForSite(selectedSite); // refresh — sender's qty just dropped
    } catch (err) {
      setTransferError(err.message || "Failed to create transfer");
    } finally {
      setTransferring(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading sites...</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Site-wise Stock</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Material currently at each site (from Store-to-Site issues)
        </p>
      </div>

      {sites.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
          No sites found for this company.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="text-xs font-semibold text-gray-500 block mb-1">Site</label>
            <select
              value={selectedSite}
              onChange={e => handleSiteChange(e.target.value)}
              className="border p-2 rounded-xl w-full text-sm outline-none bg-white"
            >
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.site_name} — {s.client_name}</option>
              ))}
            </select>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loadingStock ? (
              <div className="p-8 text-center text-gray-400">Loading stock...</div>
            ) : stockRows.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <div className="text-4xl mb-2">📦</div>
                <p>No stock recorded at this site yet.</p>
                <p className="text-xs mt-1">
                  Only issues made after Site-wise Stock Tracking launched are shown here —
                  material issued before that isn't retroactively attributed to a site.
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white text-xs">
                  <tr>
                    <th className="text-left px-4 py-3">Item</th>
                    <th className="text-left px-4 py-3">Code</th>
                    <th className="text-left px-4 py-3">Qty at Site</th>
                    <th className="text-left px-4 py-3">Unit</th>
                    <th className="text-left px-4 py-3">Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {stockRows.map(row => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="px-4 py-3 font-medium text-slate-700">{row.item_name}</td>
                      <td className="px-4 py-3 text-slate-500">{row.item_code || "—"}</td>
                      <td className="px-4 py-3 font-bold text-slate-800">{row.qty}</td>
                      <td className="px-4 py-3 text-slate-500">{row.unit}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openTransfer(row)}
                          className="text-purple-600 hover:text-purple-800 font-semibold text-xs"
                        >
                          ↪ Transfer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── SITE-TO-SITE TRANSFER MODAL ── */}
      {transferRow && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <h3 className="font-bold text-gray-800 mb-1">↪ Transfer to Another Site</h3>
            <p className="text-xs text-gray-500 mb-4">
              {transferRow.item_name} — {transferRow.qty} {transferRow.unit} available at this site.
              Deducted immediately; the destination site must accept before it's credited there.
            </p>

            {transferError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                {transferError}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Destination Site</label>
                <select
                  value={transferToSite}
                  onChange={e => setTransferToSite(e.target.value)}
                  className="border p-2 rounded-xl w-full text-sm outline-none bg-white"
                >
                  <option value="">— Select site —</option>
                  {sites.filter(s => s.id !== selectedSite).map(s => (
                    <option key={s.id} value={s.id}>{s.site_name} — {s.client_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Quantity</label>
                <input
                  type="number"
                  value={transferQty}
                  onChange={e => setTransferQty(e.target.value)}
                  max={transferRow.qty}
                  className="border p-2 rounded-xl w-full text-sm outline-none"
                  placeholder={`Max ${transferRow.qty} ${transferRow.unit}`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Remarks (optional)</label>
                <textarea
                  rows={2}
                  value={transferRemarks}
                  onChange={e => setTransferRemarks(e.target.value)}
                  className="border p-2 rounded-xl w-full text-sm outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={closeTransfer}
                className="flex-1 px-4 py-2 rounded-xl border text-sm font-semibold text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={submitTransfer}
                disabled={transferring}
                className="flex-1 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
              >
                {transferring ? "Sending..." : "Send Transfer →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}